"use client";

import { NotLoggedIn } from "@components/ui/not-logged-in/NotLoggedIn";
import { MyAccountSkeleton } from "@components/ui/skeletons/my-account-skeleton";
import type { Customer } from "@graphql/generated/types";
import { useAuth } from "@lib/auth/useAuth";
import { type CustomerLoyaltyObject } from "@store/index";
import { useGetMyProfile } from "@store/hooks";
import { TwoColumnsPage } from "templates/TwoColumnPage";
import { Box } from "@ui/components/layout/box/Box";
import styles from "@components/ui/ecco-club/faqSection.module.css";
import JoinEccoClub from "@components/ui/ecco-club/cards/JoinEccoClub";
import MemberBenefits from "@components/ui/ecco-club/cards/MemberOnlyBenefits";
import MembershipDetails from "@components/ui/ecco-club/cards/MembershipDetails";
import { Ribbon } from "@components/ui/ecco-club/ribbon";
import { isMarketWithLoyalty } from "@lib/utils/loyalty";
import type { AccountPageProps } from "templates/AccountPage";

type CustomerObject = Customer & CustomerLoyaltyObject;

export default function EccoClubClient(props: AccountPageProps) {
    const { data, isLoading: isLoadingProfile } = useGetMyProfile();
    const { isAuthenticated, isLoading } = useAuth();
    const customer = data as CustomerObject;

    const firstName = customer?.firstName || "";
    const lastName = customer?.lastName || "";
    const navigationTitle = `${firstName} ${lastName}`.trim();

    if (isLoading || isLoadingProfile) {
        return <MyAccountSkeleton />;
    }

    if (!isAuthenticated) {
        return (
            <>
                <div hidden aria-hidden="true" data-testid="myAccountProfile" />
                <NotLoggedIn />
            </>
        );
    }

    return (
        <>
            <div hidden aria-hidden="true" data-testid="myAccountEccoClub" />
            <Box className={styles.faqWrapper}>
                <TwoColumnsPage
                    showTitle={false}
                    navigationTitle={navigationTitle}
                    showNavigation={false}
                    {...props}
                >
                    {customer?.loyalty ? (
                        <MembershipDetails
                            tierLevel={customer.loyalty?.tier || ""}
                            pointsBalance={customer.loyalty?.pointBalance || 0}
                            membershipId={customer.loyalty?.cardNumber || ""}
                        />
                    ) : (
                        <JoinEccoClub />
                    )}
                </TwoColumnsPage>
            </Box>
            <Box bg="gray.50" paddingBottom={[8, 12]}>
                <MemberBenefits />
            </Box>
            {isMarketWithLoyalty && (
                <Box
                    css={{
                        width: "100%",
                        height: "9",
                        position: "relative",
                    }}
                >
                    <Ribbon />
                </Box>
            )}
        </>
    );
}
